// ─── TTS Voice Types ──────────────────────────────────────────────────────

export interface VoiceAssignment {
  sceneId: number;
  character: string;
  modelId: string;
  voice: string;
}

export interface TtsVoicePreset {
  id: string;
  name: string;
  gender: 'female' | 'male';
}

// ─── Kokoro Voices ──────────────────────────────────────────────────────

export const KOKORO_VOICES: TtsVoicePreset[] = [
  { id: 'af_heart',   name: 'Heart',   gender: 'female' },
  { id: 'af_bella',   name: 'Bella',   gender: 'female' },
  { id: 'af_jessica', name: 'Jessica', gender: 'female' },
  { id: 'af_nicole',  name: 'Nicole',  gender: 'female' },
  { id: 'af_nova',    name: 'Nova',    gender: 'female' },
  { id: 'am_adam',    name: 'Adam',    gender: 'male' },
  { id: 'am_eric',    name: 'Eric',    gender: 'male' },
  { id: 'am_michael', name: 'Michael', gender: 'male' },
];

// ─── ElevenLabs Voices ───────────────────────────────────────────────────

export const ELEVENLABS_VOICES: TtsVoicePreset[] = [
  { id: 'Rachel',    name: 'Rachel',    gender: 'female' },
  { id: 'Aria',      name: 'Aria',      gender: 'female' },
  { id: 'Sarah',     name: 'Sarah',     gender: 'female' },
  { id: 'Laura',     name: 'Laura',     gender: 'female' },
  { id: 'Charlotte', name: 'Charlotte', gender: 'female' },
  { id: 'Alice',     name: 'Alice',     gender: 'female' },
  { id: 'Matilda',   name: 'Matilda',   gender: 'female' },
  { id: 'Jessica',   name: 'Jessica',   gender: 'female' },
  { id: 'Lily',      name: 'Lily',      gender: 'female' },
  { id: 'Roger',     name: 'Roger',     gender: 'male' },
  { id: 'Charlie',   name: 'Charlie',   gender: 'male' },
  { id: 'George',    name: 'George',    gender: 'male' },
  { id: 'Callum',    name: 'Callum',    gender: 'male' },
  { id: 'River',     name: 'River',     gender: 'male' },
  { id: 'Liam',      name: 'Liam',      gender: 'male' },
  { id: 'Will',      name: 'Will',      gender: 'male' },
  { id: 'Eric',      name: 'Eric',      gender: 'male' },
  { id: 'Chris',     name: 'Chris',     gender: 'male' },
  { id: 'Brian',     name: 'Brian',     gender: 'male' },
  { id: 'Daniel',    name: 'Daniel',    gender: 'male' },
  { id: 'Bill',      name: 'Bill',      gender: 'male' },
];

// ─── Per-line / per-scene costs (must match edge function TTS_COSTS) ────
// 20261008 (owner): source of truth = supabase/functions/ai-generate/_tts.ts. kokoro/elevenlabs
// mirror its TTS_COSTS: perLine = one submit-tts call (a single typed line); perScene = the FLAT
// charge for one submit-tts-scene / submit-tts-batch call, however many lines the batch holds.
// music/sfx mirror its AUDIO_GEN_COSTS (flat per clip). The Audio ▸ Create estimate applies the
// rule in components/AudioWorkspace/scratchCredits.ts; its test pins these numbers to the server.

export const TTS_COSTS: Record<string, { perLine: number; perScene: number }> = {
  kokoro:     { perLine: 2, perScene: 10 },
  elevenlabs: { perLine: 3, perScene: 15 },
  music:      { perLine: 40, perScene: 40 },
  sfx:        { perLine: 5, perScene: 5 },
};

// ─── Helpers ─────────────────────────────────────────────────────────────

export function getVoicesForModel(modelId: string): TtsVoicePreset[] {
  if (modelId === 'kokoro') return KOKORO_VOICES;
  return ELEVENLABS_VOICES;
}

/** Build a character→voice map from saved assignments, with global fallback (sceneId=0) and auto-fill. */
export function buildVoiceMap(
  sceneId: number,
  modelId: string,
  saved: VoiceAssignment[],
  characters: string[],
): Map<string, string> {
  const map = new Map<string, string>();
  const sceneAssignments = saved.filter(a => a.sceneId === sceneId && a.modelId === modelId);
  const globalAssignments = sceneId !== 0
    ? saved.filter(a => a.sceneId === 0 && a.modelId === modelId)
    : [];

  for (const char of characters) {
    const sceneMatch = sceneAssignments.find(a => a.character === char);
    if (sceneMatch) {
      map.set(char, sceneMatch.voice);
      continue;
    }
    const globalMatch = globalAssignments.find(a => a.character === char);
    if (globalMatch) {
      map.set(char, globalMatch.voice);
      continue;
    }
  }

  // Auto-assign remaining characters
  const unassigned = characters.filter(c => !map.has(c));
  if (unassigned.length > 0) {
    const voices = getVoicesForModel(modelId);
    const used = new Set(map.values());
    const available = voices.filter(v => !used.has(v.id));
    const pool = available.length > 0 ? available : voices;
    for (let i = 0; i < unassigned.length; i++) {
      map.set(unassigned[i], pool[i % pool.length].id);
    }
  }

  return map;
}
